"use client"

import { useRef } from "react"
import clsx from "clsx"

import Slider, { useSliderState } from "@unlikelystudio/react-slider"

import type { PropsWithClassName } from "~/@types/generics"
import { Image } from "~/components/ui/Image"
import type { TProductHeader } from "~/components/ui/Product/ProductHeader/_data/serializer"
import ShopTheLookPanelTrigger from "~/components/ui/ShopTheLookPanel/components/Trigger"
import { SliderBullets } from "~/components/ui/Slider/SliderBullets"
import { VideoWithRatio } from "~/components/ui/VideoMobileDesktop"

import { hideFrom } from "~/styles/utils/show-hide"
import { breakpoints } from "~/styles/variables/breakpoints"

import * as css from "./styles.css"

const IMAGE_SIZES = [{ breakpoint: breakpoints.md, ratio: 1 / 2 }, { ratio: 1 }]

export type ProductHeaderSliderProps = PropsWithClassName<{
  medias?: TProductHeader["medias"]
}>

export function ProductHeaderSlider({ className, medias }: ProductHeaderSliderProps) {
  const customSliderRef = useRef<HTMLDivElement>(null)
  const [{ maxSlideIndex, slideIndex, enableDrag }, setSliderState] = useSliderState()

  const processedMedias = medias && medias?.length > 0 ? medias?.filter(Boolean) : []

  return (
    <div
      className={clsx(className, css.ProductHeaderSlider)}
      ref={customSliderRef}
      data-comp="UI/Product/ProductHeader/ProductHeaderSlider"
    >
      <div className={hideFrom("tablet", "block")}>
        <Slider infinite setSliderState={setSliderState} customSliderRef={customSliderRef} snap maxSlideIndexChange={1}>
          {processedMedias.map((item, index) =>
            item.media.type === "video" ? (
              <VideoWithRatio
                key={`mobile_slider_video_${index}`}
                ratio={"10/13"}
                video={item.media}
                className={css.sliderItem}
              />
            ) : (
              item.media.type === "image" && (
                <div className={css.sliderItem} key={`mobile_slider_image_${index}`}>
                  <Image
                    objectFit={({ isPNG }) => (isPNG ? "contain" : "cover")}
                    {...item.media}
                    asPlaceholder
                    priority={index === 0}
                    ratio="10/13"
                    sizesFromBreakpoints={IMAGE_SIZES}
                  />
                  {item.shopTheLook && (
                    <ShopTheLookPanelTrigger className={clsx(css.shopTheLook)} products={item.shopTheLook} />
                  )}
                </div>
              )
            )
          )}
        </Slider>
        {enableDrag && (
          <SliderBullets
            sprinklesCss={{ position: "absolute", justifyContent: "center", bottom: 20, left: 0, right: 0 }}
            currentIndex={slideIndex}
            totalItems={maxSlideIndex + 1}
          />
        )}
      </div>
      <div className={css.desktopGallery}>
        {processedMedias?.map((item, index) =>
          item.media.type === "video" ? (
            <VideoWithRatio
              key={`mobile_slider_video_${index}`}
              className={css.galleryItem}
              ratio={"10/13"}
              video={item.media}
            />
          ) : (
            <div key={`desktop_gallery_${index}`} className={css.galleryItem}>
              <Image
                objectFit={({ isPNG }) => (isPNG ? "contain" : "cover")}
                {...item.media}
                asPlaceholder
                priority={index === 0}
                ratio="10/13"
                sizesFromBreakpoints={IMAGE_SIZES}
              />
              {item.shopTheLook && (
                <ShopTheLookPanelTrigger className={clsx(css.shopTheLook)} products={item.shopTheLook} />
              )}
            </div>
          )
        )}
      </div>
    </div>
  )
}
